import { useEffect, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

import { apiClient } from '../lib/api/client';
import {
  confirmMeetingMagicLink,
  identifyForMeeting,
  registerGuest,
  requestMeetingMagicLink,
} from '../lib/meetings';
import { getSession, normalizeBackendUser, setSession } from '../lib/auth';
import { persistTodayMatch } from '../lib/meetingMatch';
import {
  createPasskey,
  getPasskeyStatus,
  passkeysAvailable,
  signInToMeetingWithPasskey,
} from '../lib/passkeys';

const LOOKUP_STORAGE_KEY = 'nbna_meeting_lookup';

function completedRegistration(data) {
  if (!data?.registration?.id || data.registration.status !== 'registered') {
    throw new Error('Attendance was not confirmed. Please try again.');
  }
  persistTodayMatch(data);
  return data?.match ? { member: data.match.member } : null;
}

async function persistRegistrationSession(data) {
  const accessToken = data?.access_token;
  const refreshToken = data?.refresh_token;
  if (!accessToken || !refreshToken) {
    throw new Error('Attendance was registered, but login could not be completed.');
  }
  const hydrated = await apiClient.get('/auth/session', {
    authToken: accessToken,
    skipAuthRefresh: true,
  });
  const user = normalizeBackendUser(hydrated?.user);
  if (!user) {
    throw new Error('Attendance was registered, but login could not be completed.');
  }
  setSession({
    user,
    accessToken,
    token: accessToken,
    refreshToken,
    tokenType: data?.token_type || 'bearer',
  }, 'SIGNED_IN');
}

function MeetingMatchPreview({ match }) {
  if (!match?.member?.id) {
    return <p className="mt-1 text-gray-700">Matched with: TBD</p>;
  }
  return (
    <div className="mt-1 space-y-1">
      <p className="text-gray-700">Matched with: {match.member.displayName || 'Member'}</p>
      <Link className="text-NBNA-primary underline" to={`/members/${match.member.id}`}>
        View profile
      </Link>
    </div>
  );
}

function RegisteredView({ match, passkeyOffer, passkeyBusy, passkeyMessage, passkeyError, onCreatePasskey }) {
  return (
    <div className="mt-6 space-y-4">
      <div className="rounded-lg border bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Registered</h2>
        <p className="mt-1 text-gray-700">You are registered for today’s meeting.</p>
      </div>
      <div className="rounded-lg border bg-white p-5 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900">Today’s Networking</h3>
        <MeetingMatchPreview match={match} />
        {match?.member?.googleBusinessProfileUrl ? (
          <>
            <p className="mt-3 text-gray-900 font-medium">Write a review</p>
            <p className="mt-1 text-gray-700">Help your match by leaving a quick review on their Google listing.</p>
            <a
              className="mt-2 inline-block text-NBNA-primary underline"
              href={match.member.googleBusinessProfileUrl}
              target="_blank"
              rel="noreferrer"
            >
              Write a Google review
            </a>
          </>
        ) : null}
      </div>
      {passkeyOffer ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900">Faster sign-in next week</h3>
          <p className="mt-1 text-gray-700">
            Create a passkey to sign in with your phone, fingerprint, face, or device PIN.
          </p>
          <button
            type="button"
            className="btn-primary mt-3"
            disabled={passkeyBusy}
            onClick={onCreatePasskey}
          >
            {passkeyBusy ? 'Creating…' : 'Create Passkey'}
          </button>
        </div>
      ) : null}
      {passkeyMessage ? (
        <p className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">{passkeyMessage}</p>
      ) : null}
      {passkeyError ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">{passkeyError}</p>
      ) : null}
      <Link className="text-NBNA-primary underline" to="/">Back to dashboard</Link>
    </div>
  );
}

export default function MeetingRegister() {
  const [searchParams] = useSearchParams();
  const qr = searchParams.get('qr') ?? '';
  const magic = searchParams.get('magic') ?? '';
  // The QR code at the door is fixed and carries no token; the server resolves
  // which meeting this is. Arriving with no token is the ordinary case now.
  const [doorStatus, setDoorStatus] = useState(null);

  const [isAuth, setIsAuth] = useState(null);
  const didAutoRegister = useRef(false);
  const didConfirmMagicLink = useRef(false);

  const [step, setStep] = useState('checking');
  const [lookup, setLookup] = useState(() => localStorage.getItem(LOOKUP_STORAGE_KEY) || '');
  const [rememberLookup, setRememberLookup] = useState(!!localStorage.getItem(LOOKUP_STORAGE_KEY));
  const [match, setMatch] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyOffer, setPasskeyOffer] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyMessage, setPasskeyMessage] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestDetails, setGuestDetails] = useState({
    name: '',
    businessName: '',
    phone: '',
    industry: '',
    servicesProvided: '',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    if (magic) {
      setIsAuth(false);
      setStep('confirming_magic_link');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await apiClient.get('/api/meetings/registration-status');
        if (cancelled) return;
        setDoorStatus(status);
        if (!status?.open) {
          setStep('closed');
          return;
        }
      } catch {
        if (cancelled) return;
        setError('Meeting registration status is unavailable. Please try again.');
        setStep('status_error');
        return;
      }

      let session = null;
      try {
        session = await getSession();
      } catch {
        session = null;
      }

      const authenticated = Boolean(session?.accessToken);
      setIsAuth(authenticated);
      setStep(authenticated ? 'registering' : 'email');
    })();
    return () => { cancelled = true; };
  }, [qr, magic]);

  useEffect(() => {
    if (step !== 'confirming_magic_link' || !magic || didConfirmMagicLink.current) return;
    didConfirmMagicLink.current = true;
    (async () => {
      try {
        const data = await confirmMeetingMagicLink({ token: magic });
        const nextMatch = completedRegistration(data);
        const accessToken = data?.access_token;
        const refreshToken = data?.refresh_token;
        const user = normalizeBackendUser(data?.user);
        if (!accessToken || !refreshToken || !user) {
          throw new Error('Attendance was registered, but login could not be completed.');
        }
        setSession({
          user,
          accessToken,
          token: accessToken,
          refreshToken,
          tokenType: data?.token_type || 'bearer',
        }, 'SIGNED_IN');
        setMatch(nextMatch);
        setStep('registered');
      } catch (err) {
        setError(err?.message || 'This meeting sign-in link is invalid or expired.');
        setStep('magic_link_error');
      }
    })();
  }, [step, magic]);

  useEffect(() => {
    if (step !== 'registering' || !isAuth || didAutoRegister.current) return;
    didAutoRegister.current = true;
    (async () => {
      try {
        const data = await apiClient.post('/api/meetings/registrations/qr', qr ? { qrToken: qr } : {});
        setMatch(completedRegistration(data));
        setStep('registered');
      } catch (err) {
        setError(err?.message || 'Registration failed. Please try again.');
        setStep('registration_error');
      }
    })();
  }, [step, isAuth, qr]);

  useEffect(() => {
    if (step !== 'registered' || !passkeysAvailable()) {
      setPasskeyOffer(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = await getPasskeyStatus();
        if (!cancelled) setPasskeyOffer(Boolean(status?.eligible && !status?.hasPasskey));
      } catch {
        if (!cancelled) setPasskeyOffer(false);
      }
    })();
    return () => { cancelled = true; };
  }, [step]);

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!lookup.trim()) return;
    setError('');
    setLoading(true);
    if (rememberLookup) localStorage.setItem(LOOKUP_STORAGE_KEY, lookup.trim());
    else localStorage.removeItem(LOOKUP_STORAGE_KEY);
    try {
      await requestMeetingMagicLink({ lookup: lookup.trim(), qrToken: qr });
      setStep('magic_link_sent');
    } catch (err) {
      setError(err?.message || 'Unable to send your meeting sign-in link. Please try again.');
    } finally { setLoading(false); }
  };

  const handlePasskeySignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const data = await signInToMeetingWithPasskey(qr || undefined);
      const nextMatch = completedRegistration(data);
      await persistRegistrationSession(data);
      setMatch(nextMatch);
      setIsAuth(true);
      setStep('registered');
    } catch (err) {
      setError(err?.message || 'Passkey sign-in could not be completed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePasskey = async () => {
    setPasskeyBusy(true);
    setPasskeyMessage('');
    setError('');
    try {
      await createPasskey();
      setPasskeyOffer(false);
      setPasskeyMessage('Passkey created. You can use it to sign in at the next meeting.');
    } catch (err) {
      setError(err?.message || 'Passkey creation could not be completed. Please try again.');
    } finally {
      setPasskeyBusy(false);
    }
  };

  const handleGuestIdentify = async (e) => {
    e.preventDefault();
    if (!guestEmail.trim()) return;
    setError('');
    setLoading(true);
    try {
      const identified = await identifyForMeeting({ email: guestEmail.trim(), qrToken: qr || undefined });
      if (identified?.flow !== 'guest') {
        setLookup(guestEmail.trim());
        setError('This email belongs to an NBNA member. Request a meeting sign-in link instead.');
        setStep('email');
        return;
      }
      setStep('guest_details');
    } catch (err) {
      setError(err?.message || 'Unable to check this email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestRegistration = async (e) => {
    e.preventDefault();
    setError('');
    if (guestDetails.password !== guestDetails.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const data = await registerGuest({
        ...guestDetails,
        email: guestEmail.trim(),
        qrToken: qr || undefined,
      });
      const nextMatch = completedRegistration(data);
      await persistRegistrationSession(data);
      setMatch(nextMatch);
      setStep('registered');
    } catch (err) {
      setError(err?.message || 'Guest registration could not be completed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Someone scanning the printed code on a Thursday deserves an answer, not a
  // dead end telling them to scan the code they just scanned.
  if (!magic && doorStatus && doorStatus.open === false) {
    return (
      <div className="max-w-3xl mx-auto py-10 px-6" data-testid="registration-closed">
        <h1 className="text-2xl font-semibold text-NBNA-primary">Meeting Registration</h1>
        <div className="mt-4 space-y-3">
          <p className="text-gray-800">{doorStatus.message}</p>
          <Link className="text-NBNA-primary underline" to="/">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none sm:max-w-2xl lg:max-w-3xl mx-auto py-10 px-4 sm:px-6">
      <h1 className="text-2xl font-semibold text-NBNA-primary mb-6">Meeting Registration</h1>

      {step === 'checking' && <p className="text-gray-500">Loading...</p>}

      {step === 'status_error' && (
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Registration status unavailable</h2>
          <p className="mt-2 text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>
          <button type="button" className="mt-4 btn-primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      )}

      {step === 'registered' && (
        <RegisteredView
          match={match}
          passkeyOffer={passkeyOffer}
          passkeyBusy={passkeyBusy}
          passkeyMessage={passkeyMessage}
          passkeyError={error}
          onCreatePasskey={handleCreatePasskey}
        />
      )}

      {step === 'registration_error' && (
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Registration not completed</h2>
          <p className="mt-2 text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>
          <button
            type="button"
            className="mt-4 btn-primary"
            onClick={() => {
              didAutoRegister.current = false;
              setError('');
              setStep('registering');
            }}
          >
            Try again
          </button>
        </div>
      )}

      {step === 'email' && (
        <form onSubmit={handleMagicLinkRequest} className="space-y-4">
          <div className="rounded-lg border bg-white p-5 shadow-sm space-y-4">
            <p className="text-gray-700">Enter your email, member name, or business name.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="member-lookup">Find your member record</label>
              <input id="member-lookup" type="text" className="input-field w-full" placeholder="Email, name, or business" value={lookup} onChange={(e) => setLookup(e.target.value)} autoComplete="off" required />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="checkbox" checked={rememberLookup} onChange={(e) => setRememberLookup(e.target.checked)} className="h-4 w-4 rounded border-gray-300 text-NBNA-primary focus:ring-NBNA-primary" />
              Remember this on this device
            </label>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}
          <button type="submit" className="btn-primary w-full" disabled={loading || !lookup.trim()}>
            {loading ? 'Sending...' : 'Email My Sign-In Link'}
          </button>
          {passkeysAvailable() ? (
            <>
              <div className="flex items-center gap-3" aria-hidden="true">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-sm text-gray-500">or</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <button
                type="button"
                className="btn-secondary w-full"
                disabled={loading}
                onClick={handlePasskeySignIn}
              >
                {loading ? 'Signing in…' : 'Sign In with Passkey'}
              </button>
            </>
          ) : null}
          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-gray-200" />
            <span className="text-sm text-gray-500">or</span>
            <span className="h-px flex-1 bg-gray-200" />
          </div>
          <button type="button" className="btn-secondary w-full" onClick={() => { setError(''); setStep('guest_email'); }}>
            Register as a Guest
          </button>
        </form>
      )}

      {step === 'guest_email' && (
        <form onSubmit={handleGuestIdentify} className="space-y-4">
          <div className="rounded-lg border bg-white p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Guest registration</h2>
            <p className="text-gray-700">First, enter your email so we can keep member and guest records separate.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-email">Guest email</label>
              <input id="guest-email" type="email" className="input-field w-full" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} autoComplete="email" required />
            </div>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}
          <button type="submit" className="btn-primary w-full" disabled={loading || !guestEmail.trim()}>
            {loading ? 'Checking...' : 'Continue as Guest'}
          </button>
          <button type="button" className="btn-secondary w-full" onClick={() => { setError(''); setStep('email'); }}>
            Back to member sign-in
          </button>
        </form>
      )}

      {step === 'guest_details' && (
        <form onSubmit={handleGuestRegistration} className="space-y-4">
          <div className="rounded-lg border bg-white p-5 shadow-sm space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Tell us about you</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-name">Name</label>
              <input id="guest-name" className="input-field w-full" value={guestDetails.name} onChange={(e) => setGuestDetails((value) => ({ ...value, name: e.target.value }))} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-business">Business name</label>
              <input id="guest-business" className="input-field w-full" value={guestDetails.businessName} onChange={(e) => setGuestDetails((value) => ({ ...value, businessName: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-phone">Phone</label>
              <input id="guest-phone" type="tel" className="input-field w-full" value={guestDetails.phone} onChange={(e) => setGuestDetails((value) => ({ ...value, phone: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-industry">Industry</label>
              <input id="guest-industry" className="input-field w-full" value={guestDetails.industry} onChange={(e) => setGuestDetails((value) => ({ ...value, industry: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-services">Services provided</label>
              <textarea id="guest-services" className="input-field w-full" value={guestDetails.servicesProvided} onChange={(e) => setGuestDetails((value) => ({ ...value, servicesProvided: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-password">Create password</label>
              <input id="guest-password" type="password" className="input-field w-full" value={guestDetails.password} onChange={(e) => setGuestDetails((value) => ({ ...value, password: e.target.value }))} minLength={8} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="guest-confirm-password">Confirm password</label>
              <input id="guest-confirm-password" type="password" className="input-field w-full" value={guestDetails.confirmPassword} onChange={(e) => setGuestDetails((value) => ({ ...value, confirmPassword: e.target.value }))} minLength={8} required />
            </div>
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>}
          <button type="submit" className="btn-primary w-full" disabled={loading}>
            {loading ? 'Registering...' : 'Register for Meeting'}
          </button>
          <button type="button" className="btn-secondary w-full" onClick={() => { setError(''); setStep('guest_email'); }}>
            Back
          </button>
        </form>
      )}

      {step === 'magic_link_sent' && (
        <div className="rounded-lg border bg-white p-5 shadow-sm space-y-3">
          <h2 className="text-lg font-semibold text-gray-900">Check your email</h2>
          <p className="text-gray-700">We sent a one-time meeting sign-in link to the email address on your member record.</p>
          <p className="text-sm text-gray-600">Open the link within 20 minutes to register your attendance.</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              setError('');
              setStep('email');
            }}
          >
            Try another member lookup
          </button>
        </div>
      )}

      {step === 'confirming_magic_link' && (
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Signing you in…</h2>
          <p className="mt-2 text-gray-700">Confirming your attendance for today’s meeting.</p>
        </div>
      )}

      {step === 'magic_link_error' && (
        <div className="rounded-lg border bg-white p-5 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900">Meeting sign-in not completed</h2>
          <p className="mt-2 text-sm text-red-700 bg-red-50 rounded-md px-3 py-2">{error}</p>
          <p className="mt-3 text-sm text-gray-600">Scan the meeting QR code again to request a new link.</p>
        </div>
      )}

      {step === 'registering' && <p className="text-gray-500">Registering your attendance...</p>}
    </div>
  );
}
